import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { ScratchCardApi, ScratchCardConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

const scratchCardFormSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    startDate: z.date({
      required_error: "Start Date is required",
    }),
    endDate: z.date({
      required_error: "End Date is required",
    }),
    rewardType: z.string().min(1, "Reward Type is required"),
    smallRewards: z.string().min(1, "Small Rewards is required"),
    mediumRewards: z.string().min(1, "Medium Rewards is required"),
    bigRewards: z.string().min(1, "Big Rewards is required"),
    useDefault: z.boolean().default(false),
  })
  .refine(
    (data) => {
      if (data.endDate && data.startDate) {
        return data.endDate >= data.startDate;
      }
      return true;
    },
    {
      message: "End Date must be after Start Date",
      path: ["endDate"],
    }
  );

type ScratchCardFormValues = z.infer<typeof scratchCardFormSchema>;

interface CreateScratchCardFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: ScratchCardConfig | null;
  isEditMode?: boolean;
}

export function CreateScratchCardForm({
  onSuccess,
  onCancel,
  initialData,
  isEditMode = false,
}: CreateScratchCardFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  // Map API rewardType (0/1) to form rewardType ("Bonus Points"/"Discount Coupons")
  const mapRewardTypeToForm = (rewardType: number): string => {
    return rewardType === 0 ? "Bonus Points" : "Discount Coupons";
  };

  // Map form rewardType to API rewardType
  const mapRewardTypeToApi = (rewardType: string): number => {
    return rewardType === "Bonus Points" ? 0 : 1;
  };

  const form = useForm<ScratchCardFormValues>({
    resolver: zodResolver(scratchCardFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      startDate: initialData?.startDate ? new Date(initialData.startDate) : undefined,
      endDate: initialData?.endDate ? new Date(initialData.endDate) : undefined,
      rewardType: initialData ? mapRewardTypeToForm(initialData.rewardType) : "",
      smallRewards: initialData?.smallReward?.toString() || "",
      mediumRewards: initialData?.mediumReward?.toString() || "",
      bigRewards: initialData?.bigReward?.toString() || "",
      useDefault: initialData?.useDefault === 1,
    },
  });

  // Sync checkbox when initialData changes (e.g., modal opens after fetch)
  React.useEffect(() => {
    if (isEditMode && initialData) {
      form.setValue("useDefault", initialData.useDefault === 1);
    }
  }, [isEditMode, initialData, form]);

  // Watch rewardType to show appropriate label
  const rewardType = form.watch("rewardType");
  const suffix =
    rewardType === "Discount Coupons"
      ? "Coupon"
      : rewardType === "Bonus Points"
      ? "Points"
      : "";

  const onSubmit = async (data: ScratchCardFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...(isEditMode && initialData ? { p_Id: initialData.id } : {}),
        p_ClientId: 1009,
        p_UseDefault: data.useDefault,
        p_Name: data.name,
        p_StartDate: data.startDate.toISOString(),
        p_EndDate: data.endDate.toISOString(),
        p_RewardType: mapRewardTypeToApi(data.rewardType),
        p_SmallReward: parseInt(data.smallRewards),
        p_MediumReward: parseInt(data.mediumRewards),
        p_BigReward: parseInt(data.bigRewards),
      };

      const response = await ScratchCardApi.createUpdateScratchCard(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: isEditMode
            ? "Scratch Card updated successfully"
            : "Scratch Card created successfully",
        });

        form.reset();
        onSuccess?.();
      }
    } catch (error: any) {
      console.error("Error saving scratch card:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to save scratch card",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter scratch card name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Start Date</FormLabel>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setStartDateOpen(false);
                      }}
                      disabled={(date) => date < new Date("1900-01-01")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>End Date</FormLabel>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setEndDateOpen(false);
                      }}
                      disabled={(date) => {
                        const startDate = form.getValues("startDate");
                        return (
                          date < new Date("1900-01-01") ||
                          (startDate && date < startDate)
                        );
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Reward Options</h3>
          <FormField
            control={form.control}
            name="rewardType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reward Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reward type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Discount Coupons">
                      Discount Coupons
                    </SelectItem>
                    <SelectItem value="Bonus Points">Bonus Points</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="smallRewards"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Small Rewards</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Enter small rewards"
                      min="0"
                      {...field}
                    />
                    {suffix && (
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {suffix}
                      </span>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mediumRewards"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Medium Rewards</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Enter medium rewards"
                      min="0"
                      {...field}
                    />
                    {suffix && (
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {suffix}
                      </span>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bigRewards"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Big Rewards</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Enter big rewards"
                      min="0"
                      {...field}
                    />
                    {suffix && (
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {suffix}
                      </span>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="useDefault"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Use Default</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Use default settings for this scratch card.
                </p>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update"
              : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
